import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle, XCircle, Send, Loader2, ClipboardList, Eye, Printer, Download, RefreshCw, ShieldCheck, UserCheck, Info, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useMemo, useRef } from "react";
// xlsx loaded dynamically to reduce bundle size

const PAGE_SIZE = 10;

interface ApprovalWorkflowProps {
  onReupload?: (context: { selectedClass: string; semester: string; examType: string; academicYear: string }) => void;
}

export function ApprovalWorkflow({ onReupload }: ApprovalWorkflowProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user, roles } = useAuth();
  const [rejectionNotes, setRejectionNotes] = useState<Record<string, string>>({});

  // Filters
  const [filterClass, setFilterClass] = useState("all");
  const [filterSemester, setFilterSemester] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Preview
  const [previewBatch, setPreviewBatch] = useState<any | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  // Verification dialog
  const [verifyDialogBatch, setVerifyDialogBatch] = useState<any | null>(null);
  const [selectedVerifier, setSelectedVerifier] = useState("");
  // Verification response dialog
  const [verificationNotes, setVerificationNotes] = useState("");
  // Delete batch
  const [deleteBatchTarget, setDeleteBatchTarget] = useState<any | null>(null);

  const isSuperadmin = roles.includes("superadmin");
  const canApprove = roles.some((r) => ["superadmin", "principal", "deputy_principal"].includes(r));
  const canSubmit = roles.some((r) => ["superadmin", "head_examination"].includes(r));

  const { data: batches = [], isLoading } = useQuery({
    queryKey: ["exam-batches", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exam_batches")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Fetch verification records
  const { data: verifications = [] } = useQuery({
    queryKey: ["exam-batch-verifications", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exam_batch_verifications")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Fetch module visibility for examination (used to show auto-grant indicator)
  const { data: examVisibility = [] } = useQuery({
    queryKey: ["module-visibility", "examination", user?.id],
    enabled: isSuperadmin,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("module_visibility")
        .select("role, visible")
        .eq("module_key", "examination");
      if (error) throw error;
      return data;
    },
  });

  // Fetch non-student staff for verifier selection
  const { data: staffUsers = [] } = useQuery({
    queryKey: ["non-student-staff", user?.id],
    enabled: isSuperadmin,
    queryFn: async () => {
      // Get all user_roles that are NOT student
      const { data: roleData, error: roleErr } = await supabase
        .from("user_roles")
        .select("user_id, role")
        .neq("role", "student");
      if (roleErr) throw roleErr;

      // Get unique user IDs
      const userIds = [...new Set((roleData || []).map((r) => r.user_id))];
      if (userIds.length === 0) return [];

      // Fetch profiles for those users
      const { data: profiles, error: profErr } = await supabase
        .from("profiles")
        .select("user_id, full_name")
        .in("user_id", userIds);
      if (profErr) throw profErr;

      // Build staff list with roles
      const roleMap = new Map<string, string[]>();
      for (const r of roleData || []) {
        if (!roleMap.has(r.user_id)) roleMap.set(r.user_id, []);
        roleMap.get(r.user_id)!.push(r.role);
      }

      return (profiles || []).map((p) => ({
        userId: p.user_id,
        fullName: p.full_name || p.user_id,
        roles: roleMap.get(p.user_id) || [],
      }));
    },
  });

  // Map batch_id → verification record
  const verificationMap = useMemo(() => {
    const map = new Map<string, any>();
    // verifications ni DESC kwa created_at — shika ya kwanza tu (= mpya zaidi) kwa kila batch
    for (const v of verifications) {
      if (!map.has(v.batch_id)) map.set(v.batch_id, v);
    }
    return map;
  }, [verifications]);

  // Resolve verifier names
  const verifierNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of staffUsers) {
      map.set(s.userId, s.fullName);
    }
    return map;
  }, [staffUsers]);

  // Distinct filter options
  const classOptions = useMemo(() => [...new Set(batches.map((b) => b.class))].sort(), [batches]);
  const semesterOptions = useMemo(() => [...new Set(batches.map((b) => b.semester))].sort(), [batches]);

  // Filtered + paginated
  const filtered = useMemo(() => {
    return batches.filter((b) => {
      if (filterClass !== "all" && b.class !== filterClass) return false;
      if (filterSemester !== "all" && b.semester !== filterSemester) return false;
      if (filterStatus !== "all" && b.status !== filterStatus) return false;
      if (searchText) {
        const s = searchText.toLowerCase();
        if (!b.class.toLowerCase().includes(s) && !b.exam_type.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [batches, filterClass, filterSemester, filterStatus, searchText]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Reset page when filters change
  useMemo(() => setCurrentPage(1), [filterClass, filterSemester, filterStatus, searchText]);

  // Preview query
  const { data: previewResults = [], isLoading: previewLoading } = useQuery({
    queryKey: ["batch-preview", previewBatch?.id, user?.id],
    enabled: !!previewBatch,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exam_results")
        .select("*, courses(course_name, course_code)")
        .eq("batch_id", previewBatch.id)
        .order("form_iv_index");
      if (error) throw error;
      return data;
    },
  });

  // Group preview results by student for display
  const previewGrouped = useMemo(() => {
    if (!previewResults.length) return [];
    const map = new Map<string, { name: string; formIv: string; modules: any[]; gpa: string; overallRemark: string }>();
    for (const r of previewResults) {
      const key = r.form_iv_index || r.student_id || r.id;
      if (!map.has(key)) {
        map.set(key, { name: key, formIv: r.form_iv_index || "", modules: [], gpa: r.gpa || "", overallRemark: r.overall_remark || "" });
      }
      const entry = map.get(key)!;
      const examParts = (r.exam_type || "").split(" - ");
      const moduleCode = examParts.length > 1 ? examParts.slice(1).join(" - ") : r.exam_type;
      entry.modules.push({
        code: moduleCode,
        grade: r.grade || (r.marks != null ? String(r.marks) : ""),
        remark: r.remark || "",
      });
    }
    return Array.from(map.values());
  }, [previewResults]);

  // Helper: check if batch has results before allowing status transitions
  const checkBatchHasResults = async (batchId: string): Promise<boolean> => {
    const { count, error } = await supabase
      .from("exam_results")
      .select("id", { count: "exact", head: true })
      .eq("batch_id", batchId);
    if (error) return false;
    return (count ?? 0) > 0;
  };

  const updateStatus = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      // Guard: block status transitions for empty batches
      const guardedStatuses = ["pending_approval", "approved", "pending_verification", "published"];
      if (guardedStatuses.includes(status)) {
        const hasResults = await checkBatchHasResults(id);
        if (!hasResults) {
          throw new Error("This batch has no saved results. Re-upload the results before continuing.");
        }
      }

      const update: { status: string; approved_by?: string; approved_at?: string; rejection_notes?: string | null } = { status };
      if (status === "approved") {
        update.approved_by = user?.id;
        update.approved_at = new Date().toISOString();
      }
      if (status === "rejected") {
        update.rejection_notes = notes || null;
      }
      const { error } = await supabase.from("exam_batches").update(update).eq("id", id);
      if (error) throw error;

      if (status === "published") {
        const { error: resultsError } = await supabase
          .from("exam_results")
          .update({ status: "published" })
          .eq("batch_id", id)
          .neq("status", "published");
        if (resultsError) throw resultsError;
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["exam-batches"] });
      queryClient.invalidateQueries({ queryKey: ["exam-batch-verifications"] });
      if (variables.status === "published") {
        queryClient.invalidateQueries({ queryKey: ["exam-results"] });
        queryClient.invalidateQueries({ queryKey: ["my-exam-results"] });
        queryClient.invalidateQueries({ queryKey: ["result-access"] });
      }
      toast({
        title: variables.status === "published" ? "Batch published" : "Batch status updated",
        description: variables.status === "published" ? "Student results are now visible." : undefined,
      });
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Send for verification mutation
  const sendForVerification = useMutation({
    mutationFn: async ({ batchId, verifierUserId }: { batchId: string; verifierUserId: string }) => {
      // Guard: block sending empty batches for verification
      const hasResults = await checkBatchHasResults(batchId);
      if (!hasResults) {
        throw new Error("This batch has no saved results. Re-upload the results before sending for verification.");
      }
      // Check if verifier already has examination module visibility
      const verifier = staffUsers.find((s) => s.userId === verifierUserId);
      const verifierRoles = verifier?.roles || [];
      let autoGranted = false;

      if (verifierRoles.length > 0) {
        // Check which roles already have examination visibility set to true
        const { data: allExamVis } = await supabase
          .from("module_visibility")
          .select("id, role, visible")
          .eq("module_key", "examination");
        
        const visMap = new Map((allExamVis || []).map((v: any) => [v.role, v]));
        const rolesNeedingGrant: string[] = [];

        for (const role of verifierRoles) {
          const existing = visMap.get(role);
          if (!existing) {
            // No row exists — insert
            const { error } = await supabase.from("module_visibility").insert({
              module_key: "examination",
              role: role as any,
              visible: true,
            });
            if (error) throw error;
            rolesNeedingGrant.push(role);
          } else if (!existing.visible) {
            // Row exists but visible=false — update to true
            const { error } = await supabase.from("module_visibility")
              .update({ visible: true })
              .eq("id", existing.id);
            if (error) throw error;
            rolesNeedingGrant.push(role);
          }
          // If already visible=true, nothing to do
        }

        if (rolesNeedingGrant.length > 0) {
          autoGranted = true;
        }
      }

      // Insert verification record
      const { error: insErr } = await supabase.from("exam_batch_verifications").insert({
        batch_id: batchId,
        assigned_by: user!.id,
        assigned_to: verifierUserId,
        status: "pending",
        auto_granted_access: autoGranted,
      } as any);
      if (insErr) throw insErr;

      // Notify the verifier
      const batch = batches.find((b) => b.id === batchId);
      const batchLabel = batch ? `${batch.class} — ${batch.semester} — ${batch.exam_type}` : batchId;
      await supabase.rpc("send_notification_to_user", {
        _target_user_id: verifierUserId,
        _title: "Results Verification Assigned",
        _message: `You have been assigned to verify results for ${batchLabel}. Please review in the Examination module.`,
        _type: "info",
        _link: "/examination",
      });

      // Update batch status
      const { error: updErr } = await supabase
        .from("exam_batches")
        .update({ status: "pending_verification" })
        .eq("id", batchId);
      if (updErr) throw updErr;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exam-batches"] });
      queryClient.invalidateQueries({ queryKey: ["exam-batch-verifications"] });
      queryClient.invalidateQueries({ queryKey: ["module-visibility"] });
      toast({ title: "Sent for verification", description: "The assigned user will review the results." });
      setVerifyDialogBatch(null);
      setSelectedVerifier("");
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Confirm/reject verification mutation
  const respondVerification = useMutation({
    mutationFn: async ({ verificationId, action, notes }: { verificationId: string; batchId: string; action: "verified" | "rejected"; notes?: string }) => {
      const { error } = await (supabase as any).rpc("complete_exam_batch_verification", {
        _verification_id: verificationId,
        _action: action,
        _notes: notes || null,
      });
      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["exam-batches"] });
      queryClient.invalidateQueries({ queryKey: ["exam-batch-verifications"] });
      queryClient.invalidateQueries({ queryKey: ["module-visibility"] });
      queryClient.invalidateQueries({ queryKey: ["batch-preview"] });
      toast({
        title: variables.action === "verified" ? "Verification confirmed" : "Verification rejected",
        description: variables.action === "verified" ? "Results are now verified and ready for publishing." : "Results sent back for review.",
      });
      setVerificationNotes("");
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const reuploadMutation = useMutation({
    mutationFn: async (batch: any) => {
      const { error: delErr } = await supabase.from("exam_results").delete().eq("batch_id", batch.id);
      if (delErr) throw delErr;
      const { error: updErr } = await supabase.from("exam_batches").update({ status: "draft" }).eq("id", batch.id);
      if (updErr) throw updErr;
      return batch;
    },
    onSuccess: (batch) => {
      queryClient.invalidateQueries({ queryKey: ["exam-batches"] });
      queryClient.invalidateQueries({ queryKey: ["exam-results"] });
      toast({ title: "Batch reset", description: "You can now re-upload results for this batch." });
      if (onReupload) {
        onReupload({
          selectedClass: batch.class,
          semester: batch.semester,
          examType: batch.exam_type,
          academicYear: batch.academic_year,
        });
      }
    },
    onError: (e: any) => toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const deleteBatchMutation = useMutation({
    mutationFn: async (batchId: string) => {
      const { error: resErr } = await supabase.from("exam_results").delete().eq("batch_id", batchId);
      if (resErr) throw resErr;
      const { error: verErr } = await supabase.from("exam_batch_verifications").delete().eq("batch_id", batchId);
      if (verErr) throw verErr;
      const { error: batchErr } = await supabase.from("exam_batches").delete().eq("id", batchId);
      if (batchErr) throw batchErr;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exam-batches"] });
      queryClient.invalidateQueries({ queryKey: ["exam-results"] });
      queryClient.invalidateQueries({ queryKey: ["exam-batch-verifications"] });
      toast({ title: "Batch deleted", description: "The batch and all associated results have been permanently deleted." });
      setDeleteBatchTarget(null);
    },
    onError: (e: any) => {
      toast({ title: "Delete failed", description: e.message, variant: "destructive" });
      setDeleteBatchTarget(null);
    },
  });

  const handleDownloadExcel = async (batch: any) => {
    try {
      const { data, error } = await supabase
        .from("exam_results")
        .select("*")
        .eq("batch_id", batch.id)
        .order("form_iv_index");
      if (error) throw error;
      if (!data || data.length === 0) {
        toast({ title: "No results", description: "No results found for this batch.", variant: "destructive" });
        return;
      }

      const studentMap = new Map<string, any>();
      const moduleCodes = new Set<string>();
      for (const r of data) {
        const key = r.form_iv_index || r.student_id || r.id;
        if (!studentMap.has(key)) {
          studentMap.set(key, { studentId: key, formIv: r.form_iv_index || "", gpa: r.gpa || "", remarks: r.overall_remark || r.remark || "", modules: {} });
        }
        const examParts = (r.exam_type || "").split(" - ");
        const code = examParts.length > 1 ? examParts.slice(1).join(" - ") : r.exam_type || "";
        moduleCodes.add(code);
        studentMap.get(key)!.modules[code] = r.grade || (r.marks != null ? r.marks : "");
      }

      const codes = Array.from(moduleCodes).sort();
      const rows: any[] = [];
      let sn = 1;
      for (const [, stu] of studentMap) {
        const row: any = { SN: sn++, "Student ID": stu.studentId, "Form IV Index": stu.formIv };
        for (const c of codes) row[c] = stu.modules[c] || "";
        row["GPA"] = stu.gpa;
        row["Remarks"] = stu.remarks;
        rows.push(row);
      }

      const XLSX = await import("xlsx");
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Results");
      XLSX.writeFile(wb, `${batch.class}_${batch.semester}_${batch.exam_type}.xlsx`);
    } catch (e: any) {
      toast({ title: "Download failed", description: e.message, variant: "destructive" });
    }
  };

  const handlePrint = async (batch: any) => {
    setPreviewBatch(batch);
    setTimeout(() => {
      window.print();
    }, 1000);
  };

  // Check if current user is the assigned verifier for a batch
  const isAssignedVerifier = (batchId: string) => {
    const v = verificationMap.get(batchId);
    return v && v.assigned_to === user?.id && v.status === "pending";
  };

  const getVerification = (batchId: string) => verificationMap.get(batchId);

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="h-4 w-4 text-primary" />
            Approval Workflow
          </CardTitle>
          <CardDescription>Track and approve result batches</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-end">
            <div className="w-36">
              <Select value={filterClass} onValueChange={setFilterClass}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Class" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Classes</SelectItem>
                  {classOptions.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <Select value={filterSemester} onValueChange={setFilterSemester}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Semester" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Semesters</SelectItem>
                  {semesterOptions.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_approval">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending_verification">Pending Verification</SelectItem>
                  <SelectItem value="verified">Verified</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Input
              placeholder="Search class/exam type..."
              className="h-8 text-xs w-44"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No result batches found</p>
          ) : (
            <>
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Class</TableHead>
                      <TableHead>Semester</TableHead>
                      <TableHead>Exam Type</TableHead>
                      <TableHead>Year</TableHead>
                      <TableHead>Students</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                      <TableHead>Tools</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginated.map((b, idx) => {
                      const verification = getVerification(b.id);
                      const isMyVerification = isAssignedVerifier(b.id);

                      return (
                        <TableRow key={b.id}>
                          <TableCell className="text-muted-foreground text-xs">
                            {(currentPage - 1) * PAGE_SIZE + idx + 1}
                          </TableCell>
                          <TableCell className="font-medium">{b.class}</TableCell>
                          <TableCell>{b.semester}</TableCell>
                          <TableCell>{b.exam_type}</TableCell>
                          <TableCell>{b.academic_year}</TableCell>
                          <TableCell>{b.total_students}</TableCell>
                          <TableCell>
                            <StatusBadge status={b.status} />
                            {b.status === "pending_verification" && verification && (
                              <p className="text-[10px] text-muted-foreground mt-0.5">
                                Verifier: {verifierNameMap.get(verification.assigned_to) || "Assigned"}
                              </p>
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 flex-wrap">
                              {canSubmit && b.status === "draft" && (
                                <Button size="sm" variant="outline" onClick={() => updateStatus.mutate({ id: b.id, status: "pending_approval" })} disabled={updateStatus.isPending}>
                                  <Send className="mr-1 h-3 w-3" /> Submit
                                </Button>
                              )}
                              {canApprove && b.status === "pending_approval" && (
                                <>
                                  <Button size="sm" variant="outline" className="text-green-700" onClick={() => updateStatus.mutate({ id: b.id, status: "approved" })} disabled={updateStatus.isPending}>
                                    <CheckCircle className="mr-1 h-3 w-3" /> Approve
                                  </Button>
                                  <div className="flex items-center gap-1">
                                    <Input
                                      placeholder="Reason..."
                                      className="h-8 text-xs w-28"
                                      value={rejectionNotes[b.id] || ""}
                                      onChange={(e) => setRejectionNotes((p) => ({ ...p, [b.id]: e.target.value }))}
                                    />
                                    <Button size="sm" variant="outline" className="text-red-700" onClick={() => updateStatus.mutate({ id: b.id, status: "rejected", notes: rejectionNotes[b.id] })} disabled={updateStatus.isPending}>
                                      <XCircle className="mr-1 h-3 w-3" /> Reject
                                    </Button>
                                  </div>
                                </>
                              )}
                              {canSubmit && b.status === "rejected" && (
                                <div className="space-y-1">
                                  <p className="text-xs text-red-600">{b.rejection_notes || "No reason provided"}</p>
                                  <Button size="sm" variant="outline" onClick={() => updateStatus.mutate({ id: b.id, status: "draft" })} disabled={updateStatus.isPending}>
                                    Return to Draft
                                  </Button>
                                </div>
                              )}

                              {/* Send for Verification — superadmin only, when approved */}
                              {b.status === "approved" && isSuperadmin && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-blue-700"
                                  onClick={() => {
                                    setVerifyDialogBatch(b);
                                    setSelectedVerifier("");
                                  }}
                                >
                                  <UserCheck className="mr-1 h-3 w-3" /> Send for Verification
                                </Button>
                              )}

                              {/* Verifier actions — when pending_verification and I'm the verifier */}
                              {b.status === "pending_verification" && isMyVerification && verification && (
                                <div className="flex items-center gap-1 flex-wrap">
                                  <Textarea
                                    placeholder="Verification notes..."
                                    className="h-8 text-xs w-36 min-h-0"
                                    value={verificationNotes}
                                    onChange={(e) => setVerificationNotes(e.target.value)}
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-700"
                                    onClick={() => respondVerification.mutate({
                                      verificationId: verification.id,
                                      batchId: b.id,
                                      action: "verified",
                                      notes: verificationNotes,
                                    })}
                                    disabled={respondVerification.isPending}
                                  >
                                    <ShieldCheck className="mr-1 h-3 w-3" /> Confirm Verified
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-red-700"
                                    onClick={() => respondVerification.mutate({
                                      verificationId: verification.id,
                                      batchId: b.id,
                                      action: "rejected",
                                      notes: verificationNotes,
                                    })}
                                    disabled={respondVerification.isPending}
                                  >
                                    <XCircle className="mr-1 h-3 w-3" /> Reject
                                  </Button>
                                </div>
                              )}

                              {/* Publish — superadmin only, when verified */}
                              {b.status === "verified" && isSuperadmin && (
                                <Button size="sm" onClick={() => updateStatus.mutate({ id: b.id, status: "published" })} disabled={updateStatus.isPending}>
                                  Publish
                                </Button>
                              )}

                              {b.status === "published" && (
                                <span className="text-xs text-green-600 font-medium">✓ Published</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button size="icon" variant="ghost" className="h-7 w-7" title="Preview" onClick={() => setPreviewBatch(b)}>
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" title="Print" onClick={() => handlePrint(b)}>
                                <Printer className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-7 w-7" title="Download Excel" onClick={() => handleDownloadExcel(b)}>
                                <Download className="h-3.5 w-3.5" />
                              </Button>
                              {isSuperadmin && b.status === "published" && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 text-xs text-orange-700"
                                  title="Re-upload"
                                  onClick={() => {
                                    if (confirm("This will delete all results for this batch and reset it to draft. Continue?")) {
                                      reuploadMutation.mutate(b);
                                    }
                                  }}
                                  disabled={reuploadMutation.isPending}
                                >
                                  <RefreshCw className="mr-1 h-3 w-3" /> Re-upload
                                </Button>
                              )}
                              {isSuperadmin && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-destructive hover:text-destructive"
                                  title="Delete batch"
                                  onClick={() => setDeleteBatchTarget(b)}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <PaginationItem key={page}>
                        <PaginationLink isActive={page === currentPage} onClick={() => setCurrentPage(page)} className="cursor-pointer">
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    <PaginationItem>
                      <PaginationNext
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Send for Verification Dialog */}
      <Dialog open={!!verifyDialogBatch} onOpenChange={(open) => !open && setVerifyDialogBatch(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" />
              Send for Verification
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Assign a staff member to verify results for <strong>{verifyDialogBatch?.class}</strong> — {verifyDialogBatch?.semester} — {verifyDialogBatch?.exam_type}
            </p>
            <Select value={selectedVerifier} onValueChange={setSelectedVerifier}>
              <SelectTrigger>
                <SelectValue placeholder="Select verifier..." />
              </SelectTrigger>
              <SelectContent className="max-h-[60vh] overflow-y-auto">
                {staffUsers
                  .filter((s) => s.userId !== user?.id)
                  .map((s) => (
                    <SelectItem key={s.userId} value={s.userId}>
                      {s.fullName} ({s.roles.map((r) => r.replace(/_/g, " ")).join(", ")})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            {(() => {
              if (!selectedVerifier) return null;
              const verifier = staffUsers.find((s) => s.userId === selectedVerifier);
              if (!verifier) return null;
              const visibleRoles = new Set(examVisibility.filter((v) => v.visible).map((v) => v.role as string));
              const hasAccess = verifier.roles.some((r) => visibleRoles.has(r));
              if (hasAccess) return null;
              return (
                <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 p-2.5 text-xs text-blue-800 dark:text-blue-300">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    <strong>{verifier.fullName}</strong> does not currently have access to the Examination module. Access will be granted automatically and revoked once verification is complete.
                  </span>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVerifyDialogBatch(null)}>Cancel</Button>
            <Button
              disabled={!selectedVerifier || sendForVerification.isPending}
              onClick={() => {
                if (verifyDialogBatch) {
                  sendForVerification.mutate({ batchId: verifyDialogBatch.id, verifierUserId: selectedVerifier });
                }
              }}
            >
              {sendForVerification.isPending && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewBatch} onOpenChange={(open) => !open && setPreviewBatch(null)}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              Results Preview — {previewBatch?.class} | {previewBatch?.semester} | {previewBatch?.exam_type}
            </DialogTitle>
          </DialogHeader>
          <div ref={printRef}>
            {previewLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : previewGrouped.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No results in this batch</p>
            ) : (
              <div className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Form IV Index</TableHead>
                      {previewGrouped[0]?.modules.map((m: any, i: number) => (
                        <TableHead key={i} className="text-xs">{m.code}</TableHead>
                      ))}
                      <TableHead>GPA</TableHead>
                      <TableHead>Overall Remark</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewGrouped.map((stu, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-xs text-muted-foreground">{idx + 1}</TableCell>
                        <TableCell className="font-medium text-xs">{stu.formIv || stu.name}</TableCell>
                        {stu.modules.map((m: any, i: number) => (
                          <TableCell key={i} className="text-xs">
                            <div>{m.grade}</div>
                            <div className={`text-[10px] ${m.remark === "SUPP" ? "text-orange-600" : m.remark === "FAIL" ? "text-red-600" : "text-green-600"}`}>
                              {m.remark}
                            </div>
                          </TableCell>
                        ))}
                        <TableCell className="font-medium text-xs">{stu.gpa}</TableCell>
                        <TableCell className={`text-xs font-medium ${stu.overallRemark?.toUpperCase().includes("SUPP") ? "text-orange-600" : stu.overallRemark?.toUpperCase().includes("FAIL") ? "text-red-600" : "text-green-600"}`}>
                          {stu.overallRemark}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Batch Confirmation */}
      <AlertDialog open={!!deleteBatchTarget} onOpenChange={(open) => !open && setDeleteBatchTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Batch Permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the batch
              {deleteBatchTarget && ` "${deleteBatchTarget.class} — ${deleteBatchTarget.semester} — ${deleteBatchTarget.exam_type}"`}
              {" "}along with <strong>all associated results and verification records</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteBatchTarget && deleteBatchMutation.mutate(deleteBatchTarget.id)}
              disabled={deleteBatchMutation.isPending}
            >
              {deleteBatchMutation.isPending ? "Deleting..." : "Delete Permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
